/**
 * ч.1427 → ч.1433: підпис пачкою. Позначені в журналі вхідні підписуються в браузері по черзі одним
 * зчитаним ключем: пароль вводиться раз, у блоці «Ключі КЕП». Ключ не покидає браузер. Той самий шлях,
 * що й підпис одного документа, тож збій одного не зупиняє решту.
 */
import { useState } from 'react';
import { AxiosError } from 'axios';
import { api } from '@/api/client';
import { attachments } from '@/api/attachments';
import BodyPortal from '@/shared/components/BodyPortal';
import {
  activateKey, activeKeyId, bytesToBase64, keyCode, listKeys, loadEuSign, pickKeyForCode, signDataInternalCadesT,
} from '@/shared/eusign/eusign';

export interface WaitingRow {
  id: string; organization_id: string; subject: string | null; peer_org_name: string | null; sender_name: string | null;
  /** ключ якого коду потрібен: сервер знає організацію запису, навіть якщо її немає у списку сторінки */
  organization_code: string | null;
  sign_token?: string | null; attachment_id: string | null;
}

function errText(e: unknown): string {
  const d = (e as AxiosError<{ detail?: unknown }>)?.response?.data?.detail;
  return typeof d === 'string' ? d : ((e as { message?: string })?.message ?? 'Не вдалося підписати');
}

export default function EdoBatchSignDialog({ rows: picked, onClose, onChanged }: {
  rows: WaitingRow[]; onClose: () => void; onChanged: () => void;
}) {
  // Перелік фіксується при відкритті: автооновлення журналу не має підміняти його під час підпису.
  const [rows, setRows] = useState(picked);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [failed, setFailed] = useState<Record<string, string>>({});
  const [finished, setFinished] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const busy = progress !== null && !finished;

  const run = async () => {
    setError(null);
    setFailed({});
    setFinished(false);
    try {
      await loadEuSign();
    } catch (e) {
      setError(errText(e));
      return;
    }
    if (!listKeys().length) {
      setError('Спершу зчитайте ключ КЕП у блоці «Ключі КЕП» вгорі сторінки: пароль вводиться один раз.');
      return;
    }
    const total = rows.length;
    setProgress({ done: 0, total });
    const keyOfOrg = new Map<string, string>();
    let active = '';
    const errors: Record<string, string> = {};
    let done = 0;
    for (const row of rows) {
      try {
        let keyId = keyOfOrg.get(row.organization_id);
        if (!keyId) {
          const owner = await pickKeyForCode(row.organization_code ?? '', row.sender_name ?? 'вашої організації');
          keyId = listKeys().find((k) => k.owner.serial === owner.serial)?.id ?? activeKeyId() ?? listKeys()[0]!.id;
          keyOfOrg.set(row.organization_id, keyId);
        }
        if (keyId !== active) {
          await activateKey(keyId);
          active = keyId;
        }
        const chosenKey = listKeys().find((k) => k.id === keyId)!;
        const blob = await attachments.downloadBlob(row.attachment_id!);
        const { signature } = await signDataInternalCadesT(new Uint8Array(await blob.arrayBuffer()));
        await api.post(`/edo-public/sign/${row.sign_token}/submit`, {
          signature_b64: bytesToBase64(signature), signer_name: chosenKey.owner.subjCN ?? '',
          signer_edrpou: keyCode(chosenKey.owner),
        }, { headers: { 'X-Silent-422': '1' } });
      } catch (e) {
        errors[row.id] = errText(e);
        setFailed({ ...errors });
      }
      done += 1;
      setProgress({ done, total });
    }
    setFinished(true);
    onChanged();
    setRows((prev) => prev.filter((r) => errors[r.id]));
  };

  const signedCount = progress ? progress.done - Object.keys(failed).length : 0;
  return (
    <BodyPortal>
      <div role="dialog" aria-modal="true" aria-label="Підпис пачкою" onClick={() => { if (!busy) onClose(); }}
        style={{ position: 'fixed', inset: 0, background: 'var(--overlay)', zIndex: 1100, display: 'flex',
          justifyContent: 'center', alignItems: 'center', padding: '1rem' }}>
        <div className="card" onClick={(e) => e.stopPropagation()}
          style={{ width: 720, maxWidth: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column', margin: 0 }}>
          <h3 style={{ margin: '0 0 0.3rem' }}>Підпис пачкою</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', margin: '0 0 0.6rem' }}>
            Усі обрані документи буде підписано ключем, зчитаним у блоці «Ключі КЕП». Пароль ключа вводиться один раз.
          </p>
          {error && <div className="mc__panel mc__panel--warn" role="alert" style={{ marginBottom: '0.5rem' }}>{error}</div>}
          {progress && (
            <div role="status" style={{ marginBottom: '0.5rem', fontSize: '0.9rem' }}>
              {finished ? `Готово: підписано ${signedCount} з ${progress.total}.` : `Підписуємо: ${progress.done} з ${progress.total}…`}
            </div>
          )}
          <div style={{ flex: '1 1 auto', minHeight: 80, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8, padding: '0.4rem 0.6rem' }}>
            {rows.map((r) => (
              <div key={r.id} style={{ padding: '0.2rem 0' }}>
                {r.subject ?? 'документ'} <span style={{ color: 'var(--muted)' }}>· {r.peer_org_name ?? ''}</span>
                {failed[r.id] && <div style={{ color: 'var(--badge-red-fg)', fontSize: '0.8rem' }}>{failed[r.id]}</div>}
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.6rem' }}>
            <button disabled={busy} onClick={onClose}>закрити</button>
            <button className="primary" disabled={busy || !rows.length}
              title={!rows.length ? 'Усе підписано' : undefined} onClick={() => void run()}>
              підписати {rows.length ? `(${rows.length})` : ''}
            </button>
          </div>
        </div>
      </div>
    </BodyPortal>
  );
}
